import styled, { css, keyframes, } from "../../__compat__/styled-components/main.entry.mjs.mjs";
import { injectTypography } from "../../utils/injectTypography.mjs.mjs";
import { getEasing } from "../../utils/getEasing.mjs.mjs";
import { injectSpacing } from "../../api/spacing/injectSpacing.mjs.mjs";
import { injectOSXFontSmoothing } from "../../utils/injectOSXFontSmoothing.mjs.mjs";
const animationDuration = 250;
/* ⚠️ This factor is the result of dividing `copy2` by `copy1` font size for
 * decrease and vice versa for increase.
 * This is necessary since `transform: scale()` does not work with units, so the
 * factor needed to be hard coded and must be updated when typography
 * tokens change.
 */
const widthScaleFactorDecrease = 12 / 16;
const widthScaleFactorIncrease = 16 / 12;
const floatKeyframes = ({ theme }, reverse = false) => {
    const { lineHeight, fontSize: fontSizeBig } = theme.responsive.typography.copy1;
    const { fontSize: fontSizeSmall } = theme.responsive.typography.copy2;
    const fontSizeDelta = `(var(${fontSizeBig}) - var(${fontSizeSmall}))`;
    const verticalAlignAdjust = `${fontSizeDelta} / 2`;
    const translateY = `(var(${lineHeight}) + ${verticalAlignAdjust})`;
    const stateSmall = css `
    width: ${100 * widthScaleFactorDecrease}%;

    font-size: var(${fontSizeSmall});
  `;
    const stateBig = css `
    width: 100%;

    font-size: var(${fontSizeBig});

    transform: translateY(calc(${translateY}));
  `;
    if (reverse) {
        return keyframes `
      0% {
        ${stateSmall}
      }
      0.001% {
        width: 100%;

        font-size: var(${fontSizeBig});

        transform: 
          translateY(calc(${verticalAlignAdjust}))
          scale(${widthScaleFactorDecrease});
      }
      100% {
        ${stateBig}
      }
    `;
    }
    return keyframes `
    0% {
      ${stateBig}
    }
    0.001% {
      width: ${100 * widthScaleFactorDecrease}%;

      font-size: var(${fontSizeSmall});

      transform:
        translateY(calc(${translateY} - ${fontSizeDelta}))
        scale(${widthScaleFactorIncrease});
    }
    100% {
      ${stateSmall}
    }
  `;
};
const injectAnimationStyles = (props) => {
    switch (props.variant) {
        case 'scale':
            return css `
        transform: scale(${widthScaleFactorIncrease});

        /* Inverse of the above scale factor to retain it's original size */
        ${StyledOptionalText} {
          transform: scale(${widthScaleFactorDecrease}) translateY(-0.1ex);
        }
      `;
        case 'float':
            return css `
        animation-name: ${floatKeyframes(props, true)};

        /* Inverse of the above scale factor to retain it's original size */
        ${StyledOptionalText} {
          transform: scale(${widthScaleFactorDecrease}) translateY(-0.1ex);
        }
      `;
        case 'static':
        case 'default':
        default:
            return false;
    }
};
export const StyledOptionalText = styled.span.withConfig({ displayName: "auir-611444a-StyledOptionalText", componentId: "auir-611444a-1ye8d5z" }) `
  display: inline-block;

  color: var(${(props) => props.theme.colors.ui.tertiary});

  transform-origin: bottom left;

  transition-timing-function: var(${getEasing});
  transition-duration: ${animationDuration}ms;
  transition-property: transform;

  [dir='rtl'] & {
    transform-origin: bottom right;
  }
`;
const getStateColor = ({ theme, ...props }, color = 'default') => {
    if (props.$disabled)
        return theme.colors.interaction.label.disabled;
    if (props.invalid)
        return theme.colors.ui.error;
    return theme.colors.interaction.label[color];
};
export const injectCommonStyles = (props) => css `
  display: block;
  padding: 0;

  color: var(${getStateColor(props, !props.empty ? 'filled' : undefined)});

  transform-origin: top left;

  transition-timing-function: var(${getEasing});
  transition-duration: ${animationDuration}ms;

  cursor: ${props.$disabled && 'not-allowed'};

  ${injectOSXFontSmoothing(true)}
  ${injectSpacing('margin')(props)}

  [dir='rtl'] & {
    transform-origin: top right;
  }

  ${!(props.$disabled || props.$readOnly) &&
    css `
    &[for] {
      cursor: pointer;
    }
  `}

  label&:hover,
  label &:hover {
    color: var(${getStateColor(props, 'hover')});
  }

  *:focus-within > & {
    color: var(${getStateColor(props, 'focus')});
  }
`;
export const StyledStaticLabel = styled.label.withConfig({ displayName: "auir-611444a-StyledStaticLabel", componentId: "auir-611444a-b1mdh4" }) `
  ${injectCommonStyles}
  transition-property: color;
  ${(props) => props.variant === 'static'
    ? injectTypography(props, 'copy1')
    : injectTypography(props, 'copy2')}

  ${StyledOptionalText} {
    ${(props) => injectTypography(props, 'copy2')}
  }
`;
export const StyledAnimatedLabel = styled.label.withConfig({ displayName: "auir-611444a-StyledAnimatedLabel", componentId: "auir-611444a-b92iv2" }) `
  ${injectCommonStyles}

  width: ${100 * widthScaleFactorDecrease}%;
  line-height: 20px;

  transform-origin: top left;
  transition-property: ${(props) => props.variant === 'scale' && 'transform,'}
    color;

  animation: ${(props) => props.variant === 'float' &&
    css `
      ${floatKeyframes} ${animationDuration}ms var(${getEasing}) both
    `};

  cursor: ${(props) => props.$disabled && 'not-allowed'};

  ${(props) => injectTypography(props, 'copy2')}

  [dir='rtl'] & {
    transform-origin: top right;
  }

  ${(props) => props.empty &&
    css `
      *:not(:focus-within) && {
        ${injectAnimationStyles(props)}
      }
    `}
`;
//# sourceMappingURL=FormLabel.styles.mjs.mjs.map